import $ from 'jquery';
import {setFieldValue, ConfigurationError} from "@ppcp/utils";
import AbstractPayPalGateway from './AbstractPayPalGateway';

/**
 * Credit Card Gateway Implementation
 *
 * Handles credit card field creation, validation, and state management
 */
class CreditCardGateway extends AbstractPayPalGateway {

    #orderId;
    #paymentToken;
    #fields = {
        name: {
            isEmpty: true,
            isValid: true,
            isRequired: false
        },
        number: {
            isEmpty: true,
            isValid: false,
            isRequired: true
        },
        cvv: {
            isEmpty: true,
            isValid: false,
            isRequired: true
        },
        expiry: {
            isEmpty: true,
            isValid: false,
            isRequired: true
        }
    };

    #breakpoints = {
        xs: [0, 600],
        sm: [600, 900],
        md: [900, 1200],
        lg: [1200, Infinity]
    }

    constructor(props) {
        super(props);
        this.capabilities = ['place_order_button', 'create_payment_method'];
        this.cardFields = [];
        this.isFastlane = false;
        this.fastlaneToken = null;
        this.mappings = [
            ['nameField', 'NameField', this.getCardNameFieldContainer(), ''],
            ['numberField', 'NumberField', this.getNumberFieldContainer(), 'cardNumberField'],
            ['cvvField', 'CVVField', this.getCVVFieldContainer(), 'cardCvvField'],
            ['expiryField', 'ExpiryField', this.getExpiryFieldContainer(), 'cardExpiryField']
        ];
        this.#fields.name.isRequired = this.settings.cardNameRequired;
    }

    initialize() {
        this.actions.addAction('checkout_error_ppcp_card', () => this.onCheckoutError());
    }

    async canMakePayment() {
        if (!this.paypal.CardFields) {
            return false;
        }
        // Check if CardFields are eligible (available in the current environment)
        if (!this.cardElement) {
            try {
                await this.createCardElement();
            } catch (error) {
                throw new ConfigurationError(error?.message || 'Configuration error');
            }
        }
        if (!this.cardElement?.isEligible()) {
            throw new ConfigurationError('ACDC is not eligible');
        }
        return true;
    }

    /**
     * Check if card fields should be created
     */
    shouldCreateElement(container) {
        return this.needsNewCardElement();
    }

    /**
     * Create credit card fields in the container
     */
    async createPaymentElement(container, options) {
        this.createCardElement();
        this.mountCardElementFields();
        this.addBreakpointClasses();

        window.addEventListener('resize', this.onWindowResize.bind(this));

        return {cardElement: this.cardElement};
    }

    createCardElement() {
        if (this.needsNewCardElement()) {
            this.cardElement = this.paypal.CardFields(this.getCardFieldOptions());
        }
    }

    getCardFieldOptions() {
        const source = this.paypal.FUNDING.CARD;
        return {
            fundingSource: source,
            inputEvents: this.getInputEvents(),
            style: this.settings.styles,
            onApprove: (data) => this.onApprove(data),
            onCancel: (data, source) => this.onCancel(data, source),
            onError: (error) => this.onError(error, source),
            onDestroy: () => this.onDestroy(source),
            createOrder: (data, actions) => this.controller.createOrder(data, actions),
            ...(this.settings.needsSetupToken && {
                createOrder: null,
                createVaultSetupToken: () => this.controller.createVaultSetupToken()
            })
        }
    }

    needsNewCardElement() {
        let bool = false;
        if (!this.cardElement) {
            bool = true;
        } else {
            bool = this.#getDOMFields(true).some($el => $el.find('iframe').length === 0);
        }
        return bool;
    }

    mountCardElementFields() {
        if (this.cardElement && this.cardElement.isEligible()) {
            for (const [key, fieldName, el] of this.mappings) {
                const $el = $(el);
                if ($el.length) {
                    if (!$el.find('iframe').length) {
                        this[key] = this.cardElement[fieldName](this.settings.fields[key]);
                        const id = '#' + $el.attr('id');
                        this[key].render(id);
                    }
                }
            }
        }
    }

    /**
     * Validate credit card fields before form submission
     */
    validatePaymentElement() {
        let hasError = false;
        Object.keys(this.#fields).forEach(key => {
            const {
                isValid,
                isEmpty,
                isRequired
            } = this.#fields[key];
            if (isRequired && (!isValid || isEmpty)) {
                const [domField] = this.#getDOMFieldByName(key);
                const code = this.#getCardFieldErrorCodeByName(key);
                const cardField = this.#getCardFieldByName(key);
                this.showCardFieldError(code);
                domField.addClass('card-field--invalid');
                cardField.addClass('invalid');
                hasError = true;
            }
        });
        if (hasError) {
            throw new Error(this.settings.i18n.incomplete_card_form);
        }
        return true;
    }

    onWindowResize(e) {
        if (this.windowResizeTimeout) {
            clearTimeout(this.windowResizeTimeout);
        }
        this.windowResizeTimeout = setTimeout(() => this.addBreakpointClasses(), 100);
    }

    destroyPaymentElement() {
        if (this.cardElement) {
            // PayPal CardFields doesn't have a close() method like Buttons
            this.cardElement = null;
        }
    }

    getInputEvents() {
        return {
            onChange: this.onCardFieldChange.bind(this),
            onFocus: this.onCardFieldFocus.bind(this),
            onBlur: this.onCardFieldBlur.bind(this)
        }
    }

    onCardFieldChange(data) {
        const {
            fields,
            errors,
            emittedBy
        } = data;
        const [domField, field] = this.#getDOMFieldByName(emittedBy, fields);
        this.#fields[emittedBy] = {
            ...this.#fields[emittedBy],
            ...field
        };
        const {
            isEmpty = false,
            isValid = false
        } = field;
        this.hideCardFieldError(domField.attr('id'));
        if (isValid) {
            domField.removeClass('card-field--invalid');
            domField.addClass('card-field--valid');
        } else {
            domField.removeClass('card-field--valid');
        }
    }

    onCardFieldFocus(data) {
        const {
            fields,
            errors,
            emittedBy
        } = data;

        const cardField = this.#getCardFieldByName(emittedBy);
        const [domField, field] = this.#getDOMFieldByName(emittedBy, fields);
        this.mappings.forEach(entry => {
            if (this[entry[0]]) {
                this[entry[0]].removeClass('focused')
            }
        });
        this.#getDOMFields().forEach(field => {
            field.removeClass('card-field--focused')
        });
        cardField.addClass('focused');
        domField.addClass('card-field--focused')
    }

    onCardFieldBlur(data) {
        const {
            fields,
            errors,
            emittedBy
        } = data;

        const [domField, field] = this.#getDOMFieldByName(emittedBy, fields);
        const cardField = this.#getCardFieldByName(emittedBy);
        cardField.removeClass('focused');
        domField.removeClass('card-field--focused');
        const {
            isValid,
            isEmpty
        } = field;
        if (!isEmpty) {
            if (!isValid) {
                domField.removeClass('card-field--valid');
                domField.addClass('card-field--invalid');
                this.showCardFieldError(this.#getCardFieldErrorCodeByName(emittedBy));
            }
        } else {
            domField.removeClass('card-field--invalid');
            cardField.removeClass('invalid');
            this.hideCardFieldError(domField.attr('id'));
        }
    }

    #getDOMFields(excludeName = false) {
        const result = [
            excludeName ? false : $(this.getCardNameFieldContainer()),
            $(this.getNumberFieldContainer()),
            $(this.getExpiryFieldContainer()),
            $(this.getCVVFieldContainer())
        ].filter(Boolean);
        return result;
    }

    #getCardFieldByName(name) {
        const entry = this.mappings.find(entry => {
            const [key] = entry;
            if (key.slice(0, name.length) === name) {
                return true;
            }
            return false;
        });
        return this[entry[0]];
    }

    #getCardFieldErrorCodeByName(name) {
        let code;
        switch (name) {
            case 'name':
                code = 'INVALID_NAME';
                break;
            case 'number':
                code = 'INVALID_NUMBER';
                break;
            case 'expiry':
                code = 'INVALID_EXPIRY';
                break;
            case 'cvv':
                code = 'INVALID_CVV';
                break;
        }
        return code;
    }

    #getDOMFieldByName(name, fields = {}) {
        let cardField, field;
        switch (name) {
            case 'name':
                field = fields.cardNameField;
                cardField = $(this.getCardNameFieldContainer());
                break;
            case 'number':
                field = fields.cardNumberField;
                cardField = $(this.getNumberFieldContainer());
                break;
            case 'expiry':
                field = fields.cardExpiryField;
                cardField = $(this.getExpiryFieldContainer());
                break;
            case 'cvv':
                field = fields.cardCvvField;
                cardField = $(this.getCVVFieldContainer());
                break;
        }
        return [cardField, field];
    }

    /**
     * Checks the container's width and assigns a class based on established breakpoints
     */
    addBreakpointClasses() {
        const $container = $(`li.payment_method_${this.id}`);
        if ($container.length) {
            const width = $container.outerWidth(true);
            let className = '';
            Object.keys(this.#breakpoints).forEach(key => {
                $container.removeClass(`card-field-container--${key}`);
            });
            const key = Object.keys(this.#breakpoints).find((key) => {
                const [r1, r2] = this.#breakpoints[key];
                if (r1 < width && r2 > width) {
                    return key;
                }
            });
            switch (key) {
                case 'xs':
                    className = 'card-field-container--xs'
                    break;
                case 'sm':
                    className = 'card-field-container--sm'
                    break;
                case 'md':
                    className = 'card-field-container--md'
                    break;
                case 'lg':
                    className = 'card-field-container--lg'
                    break;
            }
            $container.addClass(className);
        }
    }

    showCardFieldError(code) {
        if (this.settings.i18n.error_codes[code]) {
            const text = this.settings.i18n.error_codes[code];
            let $el;
            switch (code) {
                case 'INVALID_NAME':
                    $el = $(`[data-parent='${this.getCardNameFieldContainer().slice(1)}']`);
                    break;
                case 'INVALID_NUMBER':
                    $el = $(`[data-parent='${this.getNumberFieldContainer().slice(1)}']`);
                    break;
                case 'INVALID_EXPIRY':
                    $el = $(`[data-parent='${this.getExpiryFieldContainer().slice(1)}']`);
                    break;
                case 'INVALID_CVV':
                    $el = $(`[data-parent='${this.getCVVFieldContainer().slice(1)}']`);
                    break;
            }
            if ($el) {
                $el.html(`<span class="error-content">${text}</span>`);
                $el.animate({'max-height': '100px', opacity: '1'}, '350ms');
            }
        }
    }

    showCardFieldErrors() {
        Object.keys(this.#fields).forEach(key => {
            const {
                isValid,
                isEmpty,
                isRequired
            } = this.#fields[key];
            if (isRequired && (!isValid || isEmpty)) {
                const [domField] = this.#getDOMFieldByName(key);
                const code = this.#getCardFieldErrorCodeByName(key);
                const cardField = this.#getCardFieldByName(key);
                this.showCardFieldError(code);
                domField.addClass('card-field--invalid');
                cardField.addClass('invalid');
            }
        });
    }

    hideCardFieldError(id) {
        const $el = $(`[data-parent='${id}']`);
        $el.animate({'max-height': '0', opacity: '0'}, 350);
    }

    getCardNameFieldContainer() {
        return `#ppcp-card-name`;
    }

    getNumberFieldContainer() {
        return `#ppcp-card-number`;
    }

    getCVVFieldContainer() {
        return `#ppcp-card-cvv`;
    }

    getExpiryFieldContainer() {
        return `#ppcp-card-exp`;
    }

    setOrderId(value) {
        this.#orderId = value;
    }

    getOrderId() {
        return this.#orderId;
    }

    setPaymentToken(value) {
        this.#paymentToken = value;
    }

    getPaymentToken() {
        return this.#paymentToken;
    }

    setIsFastlane(value) {
        this.isFastlane = value;
    }

    /**
     * Check if payment is ready (order/token exists)
     * Used by CheckoutController to determine if form submission should proceed
     */
    isPaymentReady() {
        return !!(this.#orderId || this.#paymentToken || this.fastlaneToken);
    }

    /**
     * Create payment method by submitting card fields
     * Called by CheckoutController when form is submitted
     * Handles both regular card submission and Fastlane token creation
     *
     * @param {Object} wcCheckoutForm - WooCommerce checkout form object for blocking UI
     */
    createPaymentMethod(wcCheckoutForm = null) {
        this.disablePlaceOrderButton();

        // Handle Fastlane vault setup token flow
        if (this.isFastlane && this.#paymentToken && this.settings.needsSetupToken && !this.fastlaneToken) {
            // Block the checkout form UI
            if (wcCheckoutForm?.blockOnSubmit) {
                wcCheckoutForm.blockOnSubmit(this.controller.getForm());
            }

            return this.controller.createPaymentToken(this.#paymentToken, {fastlane: true})
                .then(response => {
                    setFieldValue('ppcp_card_payment_token', response.id, null);
                    this.fastlaneToken = response.id;
                    // Resubmit form after Fastlane token is created
                    this.controller.submit();
                    return response;
                })
                .catch(error => {
                    // Unblock the checkout form UI on error
                    wcCheckoutForm?.$checkout_form?.removeClass('processing')?.unblock();
                    throw error;
                })
                .finally(() => {
                    // Re-enable button in case submission fails or is prevented
                    this.enablePlaceOrderButton();
                });
        }

        // Regular card field submission
        if (this.cardElement) {
            return this.cardElement.submit({}).then(() => {
                this.enablePlaceOrderButton();
            }).catch(error => {
                this.enablePlaceOrderButton();
                if (error?.message && ['INVALID_NUMBER', 'INVALID_EXPIRY', 'INVALID_CVV'].includes(error.message)) {
                    this.showCardFieldErrors(error.message);
                    throw new Error(this.settings.i18n.incomplete_card_form);
                }
                throw error;
            });
        }
    }

    enablePaymentElement() {
        this.enablePlaceOrderButton();
    }

    /**
     * Disable the place order button during payment processing
     */
    disablePlaceOrderButton() {
        this.controller.getPlaceOrderButton()?.prop('disabled', true);
    }

    /**
     * Enable the place order button after payment processing
     */
    enablePlaceOrderButton() {
        this.controller.getPlaceOrderButton()?.prop('disabled', false);
    }

    onCheckoutError() {
        setFieldValue('ppcp_card_paypal_order_id', null, null);
        setFieldValue('ppcp_card_payment_token', null, null)
    }
}

export {CreditCardGateway};
export default CreditCardGateway;